import { useState, useEffect, useRef } from 'react';
import { useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';

import useEmailPinAuth from '../../../../../../components/auth/hooks/useEmailPinAuth';
import usePhoneAuth from '../../../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../../../services/recaptcha/useGoogleRecaptcha';
import useIsNativeWebView from '../../../../../../services/native-bridge/hooks/useIsWebView';
import marketingAccountLinkMutation from '../mutations/marketing-account-link';
import { loginSuccess } from '../../../../../../actions/auth-actions';
import { AUTH_TYPE } from '../../../../../../constants/auth';
import { ACCOUNT_TYPE } from '../../../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';

/**
 * Handles the pin based authentication for an account associated with a
 * roster QR code. Supports both email and phone contact methods.
 *
 * @param {Object} params
 * @param {Object} params.account The account to authenticate.
 * @param {('email'|'phone')} [params.method] The contact method to use. When
 *   omitted, email is preferred and phone is used as a fallback.
 * @param {String} [params.emailAddress] The specific email address to use.
 *   Accounts can have multiple email addresses, so this selects which one to
 *   authenticate with. Defaults to the first available email address.
 * @param {String} [params.phoneNumber] The specific phone number to use.
 *   Defaults to the phone number of the account. Accounts without any contact
 *   method on file authenticate with a contact the user provides, which is
 *   what these two overrides carry.
 */
const useRosterQrCodeOnboarding = ({
  account,
  method,
  emailAddress: selectedEmailAddress,
  phoneNumber: selectedPhoneNumber,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set while a marketing account is being linked to the authenticated
  // identity, so the view can hold its redirect back until it completed.
  const [linkPending, setLinkPending] = useState(false);

  const emailAddress = selectedEmailAddress ?? account?.email_addresses?.[0];
  const phoneNumber = selectedPhoneNumber ?? account?.phone_number;
  // Only marketing accounts have to be linked, a standard account already is
  // the identity that is being authenticated.
  const marketingAccountId =
    account?.type === ACCOUNT_TYPE.MARKETING ? account.id : undefined;
  // Honour an explicit method when provided, otherwise prefer email and fall
  // back to phone.
  const authMethodIsPhone = method
    ? method === AUTH_TYPE.PHONE
    : !emailAddress && !!phoneNumber;

  const { onEmailAuthenticationRequest, onLoginByEmail } = useEmailPinAuth();
  const {
    loading: phoneLoading,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();
  const { onTokenRequest, recaptchaLoaded } = useGoogleRecaptcha();
  const isWebView = useIsNativeWebView();
  // Inside a native web view the recaptcha script is never injected, so there
  // is nothing to wait for and the request goes out without a token.
  const recaptchaReady = recaptchaLoaded || isWebView;
  const [marketingAccountLink] = useMutation(marketingAccountLinkMutation);

  // Links a marketing account to the freshly authenticated identity, mirroring
  // the behaviour of the MarketingAccountOnboardingView.
  const onMarketingAccountLink = async marketingAccountId => {
    await marketingAccountLink({
      variables: {
        input: {
          marketing_account_id: marketingAccountId,
        },
      },
    });
  };

  const requestPin = async (forceAllowAction = false) => {
    setLoading(true);
    setError(null);
    try {
      if (authMethodIsPhone) {
        const token = await onTokenRequest({ action: 'login' });
        await onPhoneAuthSubmit(phoneNumber, true, token, forceAllowAction);
      } else if (emailAddress) {
        await onEmailAuthenticationRequest({ emailAddress });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Tracks the contact method a pin has already been requested for, so a
  // re-render (or coming back to an option that was already used) doesn't send
  // a second pin.
  const requestedFor = useRef(null);

  useEffect(() => {
    const destination = authMethodIsPhone ? phoneNumber : emailAddress;

    // Phone pins are captcha protected, so hold the request until the recaptcha
    // is ready. Requesting before then sends an empty token and the platform
    // silently challenges the request instead of texting a pin.
    if (!destination || (authMethodIsPhone && !recaptchaReady)) {
      return;
    }

    const key = `${authMethodIsPhone ? AUTH_TYPE.PHONE : AUTH_TYPE.EMAIL}-${destination}`;

    if (requestedFor.current === key) {
      return;
    }

    requestedFor.current = key;
    requestPin();
  }, [emailAddress, phoneNumber, authMethodIsPhone, recaptchaReady]);

  // Phone verification surfaces the authenticated state before this runs, so
  // the link is flagged as pending to keep the view from redirecting while it
  // is in flight.
  const onPhoneLogin = async () => {
    try {
      await onMarketingAccountLink(marketingAccountId);
    } catch (e) {
      setError(e.message);
    } finally {
      setLinkPending(false);
    }
  };

  const onPinVerify = async code => {
    setLoading(true);
    setError(null);
    try {
      if (authMethodIsPhone) {
        // Phone verification dispatches the authenticated state on success.
        if (!marketingAccountId) {
          await onPhoneAuthVerify(phoneNumber, code);
          return;
        }

        setLinkPending(true);
        await onPhoneAuthVerify(phoneNumber, code, onPhoneLogin);
        return;
      }

      const result = await onLoginByEmail({ emailAddress, code });
      if (result?.data?.accountLoginByEmail?.success) {
        // When the authenticated account is a marketing account, link it to the
        // sign up before surfacing the authenticated state so the redirect to
        // the onboarding view only happens once linking has completed.
        if (marketingAccountId) {
          await onMarketingAccountLink(marketingAccountId);
        }
        dispatch(loginSuccess());
      }
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const onPinResend = async (forceAllowAction = false) => {
    setError(null);
    try {
      if (authMethodIsPhone) {
        const token = await onTokenRequest({ action: 'login' });
        await onResendPhoneAuthCode(phoneNumber, true, token, forceAllowAction);
      } else if (emailAddress) {
        await onEmailAuthenticationRequest({ emailAddress });
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return {
    loading: loading || phoneLoading || linkPending,
    linkPending,
    error,
    emailAddress,
    phoneNumber,
    authMethodIsPhone,
    requireHumanInput,
    blockLogin,
    onPinVerify,
    onPinResend,
    onMarketingAccountLink,
  };
};

export default useRosterQrCodeOnboarding;
